import {useEditor, EditorContent} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Heading from '@tiptap/extension-heading'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import CodeBlock from '@tiptap/extension-code-block'
import Underline from '@tiptap/extension-underline'
import {useEffect} from 'react'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Code,
  Image as ImageIcon,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo,
  Redo,
} from 'lucide-react'

type Props = {
  value: string
  onChange: (value: string) => void
}

const TiptapEditor = ({value, onChange}: Props) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
      }),
      Heading.configure({levels: [1, 2, 3]}),
      Underline,
      BulletList,
      OrderedList,
      ListItem,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      CodeBlock,
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px]',
      },
    },
    content: value,
    onUpdate({editor}) {
      onChange(editor.getHTML())
    },
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  if (!editor) return null

  const MenuButton = ({
    onClick,
    isActive,
    children,
    title,
  }: {
    onClick: () => void
    isActive?: boolean
    children: React.ReactNode
    title: string
  }) => (
    <button
      type='button'
      onClick={onClick}
      title={title}
      className={`p-2 rounded-md transition-all ${
        isActive ? 'bg-[#008080] text-white' : 'hover:bg-gray-200 text-gray-600'
      }`}
    >
      {children}
    </button>
  )

  return (
    <div className='border border-gray-300 rounded-lg overflow-hidden shadow-sm'>
      {/* Toolbar */}
      <div className='flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-200 sticky top-0 z-10'>
        <div className='flex items-center gap-1 pr-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({level: 1}).run()}
            isActive={editor.isActive('heading', {level: 1})}
            title='Heading 1'
          >
            <Heading1 className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({level: 2}).run()}
            isActive={editor.isActive('heading', {level: 2})}
            title='Heading 2'
          >
            <Heading2 className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({level: 3}).run()}
            isActive={editor.isActive('heading', {level: 3})}
            title='Heading 3'
          >
            <Heading3 className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 px-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title='Bold'
          >
            <Bold className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title='Italic'
          >
            <Italic className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            title='Underline'
          >
            <UnderlineIcon className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 px-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title='Bullet List'
          >
            <List className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title='Numbered List'
          >
            <ListOrdered className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title='Blockquote'
          >
            <Quote className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 px-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => {
              const url = prompt('Enter URL')
              if (url) editor.chain().focus().setLink({href: url}).run()
            }}
            isActive={editor.isActive('link')}
            title='Insert Link'
          >
            <LinkIcon className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => {
              const url = prompt('Enter image URL')
              if (url) editor.chain().focus().setImage({src: url}).run()
            }}
            title='Insert Image'
          >
            <ImageIcon className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            title='Code Block'
          >
            <Code className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 pl-2 ml-auto'>
          <button
            type='button'
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className='p-2 hover:bg-gray-200 text-gray-600 disabled:opacity-50'
            title='Undo'
          >
            <Undo className='w-4 h-4' />
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className='p-2 hover:bg-gray-200 text-gray-600 disabled:opacity-50'
            title='Redo'
          >
            <Redo className='w-4 h-4' />
          </button>
        </div>
      </div>

      {/* Editor Content Area */}
      <div className='p-4 bg-white'>
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

export default TiptapEditor

